import { useEffect, useRef, useState, useCallback } from "react"
import { cn } from "@vc/lib/utils"
import { GripVertical } from "lucide-react"

// Captions come only from the meeting's server-side transcription (see
// useLiveCaptions), and MeetingScreen renders this overlay only in a meeting
// that has it. There used to be a fallback here for meetings without
// transcription: the browser's own Web Speech API. It captioned only the local
// user, opened the microphone on its own and kept listening while the meeting
// had them muted (Chrome streams that audio to Google), and its translation
// sent the text to a third-party API. It was removed rather than fixed.

export interface CaptionLine {
  id: string
  speaker: string
  text: string
  ts: number
}

interface CaptionsOverlayProps {
  /** This viewer's own "show captions" toggle. */
  isActive: boolean
  /** Finished lines from the transcription server, each with its speaker. */
  entries: CaptionLine[]
}

export function CaptionsOverlay({ isActive, entries }: CaptionsOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Drag state
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragOffsetRef = useRef({ x: 0, y: 0 })
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Calculate default center-bottom position
  const getDefaultPosition = useCallback(() => {
    if (!wrapperRef.current) return { x: 0, y: 0 }
    const viewportW = window.innerWidth
    const elementW = wrapperRef.current.offsetWidth
    // Center horizontally, position near bottom
    return {
      x: Math.max(0, (viewportW - elementW) / 2),
      y: 0 // y: 0 means bottom positioning via CSS
    }
  }, [])

  // Set default position on mount
  useEffect(() => {
    if (position === null && isActive && wrapperRef.current) {
      setPosition(getDefaultPosition())
    }
  }, [position, isActive, getDefaultPosition])

  // Recalculate on window resize (only if not dragged)
  useEffect(() => {
    const handleResize = () => {
      if (!isDragging && wrapperRef.current) {
        setPosition(getDefaultPosition())
      }
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [isDragging, getDefaultPosition])

  // Auto-scroll to bottom
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [entries])

  // Drag handlers
  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    if (!wrapperRef.current) return
    const rect = wrapperRef.current.getBoundingClientRect()
    dragOffsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    }
    setIsDragging(true)
  }, [])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    handleDragStart(e.clientX, e.clientY)
  }, [handleDragStart])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    handleDragStart(touch.clientX, touch.clientY)
  }, [handleDragStart])

  const handleDragMove = useCallback((clientX: number, clientY: number) => {
    if (!isDragging) return

    const newX = clientX - dragOffsetRef.current.x
    const newY = clientY - dragOffsetRef.current.y

    // Get viewport dimensions
    const viewportW = window.innerWidth
    const viewportH = window.innerHeight

    // Get element dimensions (approximate since we don't have ref during move)
    const elementW = wrapperRef.current?.offsetWidth || 400
    const elementH = wrapperRef.current?.offsetHeight || 150

    // Constrain to viewport bounds
    const constrainedX = Math.max(0, Math.min(newX, viewportW - elementW))
    const constrainedY = Math.max(0, Math.min(newY, viewportH - elementH))

    setPosition({ x: constrainedX, y: constrainedY })
  }, [isDragging])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    handleDragMove(e.clientX, e.clientY)
  }, [handleDragMove])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    const touch = e.touches[0]
    handleDragMove(touch.clientX, touch.clientY)
  }, [handleDragMove])

  const handleDragEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Attach global drag listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleDragEnd)
      window.addEventListener("touchmove", handleTouchMove)
      window.addEventListener("touchend", handleDragEnd)

      return () => {
        window.removeEventListener("mousemove", handleMouseMove)
        window.removeEventListener("mouseup", handleDragEnd)
        window.removeEventListener("touchmove", handleTouchMove)
        window.removeEventListener("touchend", handleDragEnd)
      }
    }
  }, [isDragging, handleMouseMove, handleTouchMove, handleDragEnd])

  // Stays visible the whole time captions are on, even with nothing said yet
  // — shows the placeholder below instead of disappearing, so turning CC on
  // has visible effect immediately rather than only once someone speaks. Off
  // is still fully hidden: nothing to drag, nothing taking up space.
  if (!isActive) return null

  return (
    <div
      ref={wrapperRef}
      className={cn(
        "fixed z-40",
        isDragging ? "cursor-grabbing select-none" : "cursor-grab"
      )}
      style={{
        left: position?.x ?? "50%",
        top: position?.y ?? "auto",
        bottom: position?.y === undefined || position?.y === 0 ? "6rem" : "auto",
        transform: position === null ? "translateX(-50%)" : undefined,
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* The card carries every visible edge: the drag highlight, and (being
          `relative`) the bottom fade. The wrapper around it is wider by the
          card's `mx-4` and square-cornered, so anything drawn on it showed as
          a faint rectangle around the card while it was dragged. */}
      <div
        ref={containerRef}
        className={cn(
          "relative max-w-3xl mx-4 rounded-2xl px-6 py-4 transition-all duration-300 pointer-events-auto",
          "bg-black/40 backdrop-blur-md border border-white/10",
          "shadow-2xl",
          isDragging && "ring-2 ring-white/20"
        )}
      >
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <GripVertical className="h-4 w-4 text-white/40" />
          <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-xs text-white/50">Live Transcription</span>
        </div>

        {/* Captions text */}
        <div className="space-y-1">
          {entries.length === 0 && (
            <p className="text-white/40 text-sm italic">
              Captions appear here when someone speaks...
            </p>
          )}

          {entries.map((line, index) => (
            <p
              key={line.id}
              className={cn(
                "text-lg leading-relaxed transition-all duration-200 text-white font-medium",
                index === entries.length - 1 && "animate-in fade-in slide-in-from-bottom-2 duration-300"
              )}
            >
              <span className="text-white/50 font-semibold mr-2">{line.speaker}:</span>
              {line.text}
            </p>
          ))}
        </div>

        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-black/50 to-transparent rounded-b-2xl pointer-events-none" />
      </div>
    </div>
  )
}
